import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { USER_TYPES, PLATFORM, USER_SOURCE } from 'utils/constants/user';
import app from 'new-ui/app';
import './hcpSignup.css';
import Form, { FORM_INPUT_TYPES } from 'new-ui/Components/Form';
import ArrowCircle from 'assets/icons/arrow-circle-right.svg';
import { useTranslation } from 'utils/modifiedTranslation';
import { preProcessCondProfile } from 'modules/user/utils';
import { isEmailValid } from 'functions/isEmailValid';
import { isPhoneValid } from 'functions/formValidations/isPhoneValid';
import { signup } from 'new-ui/functions';
import useRecaptcha from 'utils/hooks/useRecaptcha';
import { POPUP_SOURCES } from 'new-ui/constants';
import { getIntercomUserCondition } from 'modules/intercom/functions';
import { HCPSignupFooter } from './HCPSignupFooter';
import { HCPSignupHeader } from './HCPSignupHeader';
import { HCPSignupFormWrapper } from './HCPSignupFormWrapper';
import { HCPSignupWrapper } from './HCPSignupWrapper';

const GA = {
  category: 'add_caregiver_popup',
  events: {
    error: 'add_caregiver popup signup error',
  },
};

export const GA_HCP = {
  category: 'HCP',
  events: {
    SIGNUP_SUCCESS: 'hcp signup succeed',
    SIGNUP_VIEWED: 'hcp signup viewed',
  },
};

const adjustPlatform = (source) => {
  if (source === POPUP_SOURCES.PATIENT) {
    return null;
  }
  return PLATFORM.HCP;
};

const getForm = ({
  indication, t, role, hideOrganization,
}) => ({
  data: {
    organization_name: '',
    email: '',
    phone: '',
    role,
    indication,
  },
  inputs: {
    ...(hideOrganization ? {} : {
      organization_name: {
        type: FORM_INPUT_TYPES.INPUT,
        label: 'Organization',
      },
    }),
    role: {
      label: 'Role',
      type: FORM_INPUT_TYPES.RADIO,
      required: true,
      options: [
        {
          title: t('create_profile.role.medical_team'),
          value: USER_TYPES.MEDICAL_TEAM,
        },
        {
          title: t('create_profile.role.patient_navigator'),
          value: USER_TYPES.PATIENT_NAVIGATOR,
        },
        {
          title: t('create_profile.role.patient'),
          value: USER_TYPES.PATIENT,
        },
        {
          title: t('create_profile.role.caregiver'),
          value: USER_TYPES.CAREGIVER,
        },
        {
          title: t('create_profile.role.other'),
          value: USER_TYPES.OTHER,
        },
      ],
    },
    email: {
      type: FORM_INPUT_TYPES.INPUT,
      required: true,
      icon: 'email',
      title: 'Email',
      placeholder: '',
      isRequired: true,
    },
    phone: {
      type: FORM_INPUT_TYPES.PHONE,
      icon: 'phone',
      title: 'Phone',
      placeholder: '651465',
      className: 'registration-phone',
    },
    indication: {
      required: true,
      title: t('add_caregiver.select_type'),
      type: FORM_INPUT_TYPES.SEARCH_INDICATION_CHOOSER,
      hidden: !!indication?.value,
    },
  },
});

const validateForm = (form, t) => {
  const {
    data: {
      email, role, indication, phone,
    } = {},
  } = form;

  if (!role) {
    return t('add_caregiver.form.error.role');
  }

  if (!email || !isEmailValid(email)) {
    return t('add_caregiver.form.error.email');
  }

  if (phone && !isPhoneValid(phone)) {
    return t('add_caregiver.form.error.phone');
  }

  if (!indication?.value) {
    return t('add_caregiver.form.error.indication');
  }

  return null;
};

// user_type can be patient, caregiver, doctor, ...
export const HCPUserSignup = (props) => {
  const {
    onSignup,
    role = '',
    indication,
    hideOrganization = false,
    source,
    profile,
  } = props;
  const { recaptcha, getRecaptchaTokenAsync } = useRecaptcha();
  const [formError, setFormError] = useState(null);
  const { t } = useTranslation();
  const { cameFrom, urlParameters } = useSelector((state) => ({
    cameFrom: state.main.cameFrom,
    urlParameters: state.main.urlParameters,
  }));

  const {
    title = t('add_caregiver.title'),
    subtitle = t('add_caregiver.subtitle'),
  } = props;

  const formRef = useRef(getForm({
    indication, role, t, hideOrganization,
  }));
  const { current: form } = formRef;

  useEffect(() => {
    app.sendGoogleAnalyticsEvent(GA_HCP.category, GA_HCP.events.SIGNUP_VIEWED);
  }, []);

  const replaceInputTypes = () => {
    const inputElement = document.querySelector('.hcp-signup-form-wrapper .form-part.email input');
    if (inputElement && inputElement.type === 'text') {
      inputElement.type = 'email';
    }
    const buttonElement = document.querySelector('.hcp-signup-form-wrapper button');
    if (buttonElement && buttonElement.type === 'button') {
      buttonElement.type = 'submit';
    }
  };

  // eslint-disable-next-line consistent-return
  const register = async () => {
    replaceInputTypes();

    const {
      data: {
        email, role: roleValue, phone, organization_name: organization, indication: formIndication,
      },
    } = form;

    const validationError = validateForm(form, t);

    if (validationError) {
      return setFormError(validationError);
    }
    setFormError(null);

    const conditionProfile = await preProcessCondProfile(
      formIndication,
      profile?.condition_profile ?? {},
    );

    app.showPreloader();

    signup({
      email,
      indication: formIndication.value,
      recaptchaToken: await getRecaptchaTokenAsync(),
      condition_profile: conditionProfile,
      info: {
        ...profile?.info ?? {},
        email,
        condition: formIndication.value,
        user_type: roleValue,
        platform: adjustPlatform(source),
        organization,
        phone,
      },
      personal: {
        ...profile?.personal ?? {},
        condition: formIndication.value,
        providerId: window._provider || '',
        providerCode: window._provider || '',
      },
      statistic: { cameFrom, urlParameters },
      onsignup: (user) => {
        app.intercom.updateUserAttributes({
          email,
          phone,
          user_type: user?.info?.user_type,
          user_source: USER_SOURCE.HCP,
          organization,
          condition: getIntercomUserCondition(formIndication),
        }, user.info.user_wix_id);
        // app.sendGoogleAnalyticsEvent(GA.category, GA.events.success, { name: 'user_signup_success' });
        app.sendGoogleAnalyticsEvent(GA_HCP.category, GA_HCP.events.SIGNUP_SUCCESS);
        app.hidePreloader();
        onSignup();
      },
      onerror: () => {
        app.hidePreloader();
        setFormError('Signup Error');
        app.sendGoogleAnalyticsEvent(GA.category, GA.events.error, { name: 'user_signup_error' });
      },
    });
  };

  return (
    <HCPSignupWrapper>
      {recaptcha}
      {form ? (
        <>
          <HCPSignupHeader
            title={title}
            subtitle={subtitle}
          />
          <HCPSignupFormWrapper error={formError}>
            <Form
              form={form}
              submit={{
                action: register,
                icon: ArrowCircle,
                title: window.t('general.save'),
              }}
            />
          </HCPSignupFormWrapper>
          <HCPSignupFooter />
        </>
      ) : null}
    </HCPSignupWrapper>
  );
};

HCPUserSignup.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  role: PropTypes.string,
  hideOrganization: PropTypes.bool,
  indication: PropTypes.shape({
    value: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    type: PropTypes.string,
  }),
  onSignup: PropTypes.func.isRequired,
  source: PropTypes.oneOf(Object.values(POPUP_SOURCES)),
  profile: PropTypes.shape({
    info: PropTypes.object.isRequired,
    personal: PropTypes.object.isRequired,
    condition_profile: PropTypes.object.isRequired,
  }),
};
